/**
 * React Query hooks for benchmark endpoints.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedQuery } from "./useAuthenticatedQuery";
import {
  fetchBenchmarkHistory,
  fetchLatestBenchmarks,
  fetchRecentPRs,
  recordBenchmark,
  type BenchmarkCreate,
} from "@/lib/exerciseApi";

const KEYS = {
  benchmarkHistory: "benchmarkHistory",
  latestBenchmarks: "latestBenchmarks",
  recentPRs: "recentPRs",
} as const;

export function useBenchmarkHistory(benchmarkName: string | null, limit = 50) {
  return useAuthenticatedQuery({
    queryKey: [KEYS.benchmarkHistory, benchmarkName, limit],
    queryFn: () => fetchBenchmarkHistory(benchmarkName!, limit),
    enabled: !!benchmarkName,
    staleTime: 2 * 60 * 1000,
  });
}

export function useLatestBenchmarks() {
  return useAuthenticatedQuery({
    queryKey: [KEYS.latestBenchmarks],
    queryFn: fetchLatestBenchmarks,
    staleTime: 2 * 60 * 1000,
  });
}

export function useRecentPRs(limit = 20) {
  return useAuthenticatedQuery({
    queryKey: [KEYS.recentPRs, limit],
    queryFn: () => fetchRecentPRs(limit),
    staleTime: 2 * 60 * 1000,
  });
}

export function useRecordBenchmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BenchmarkCreate) => recordBenchmark(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.latestBenchmarks] });
      qc.invalidateQueries({ queryKey: [KEYS.recentPRs] });
      qc.invalidateQueries({ queryKey: [KEYS.benchmarkHistory] });
    },
  });
}
